/* TTT FBA Buybox — tier + scent picker state machine.
 * Cart adds intentionally carry no line-item properties: the
 * ttt-network-interceptor section on the template tags them. */
(() => {
  if (customElements.get('ttt-fba-buybox')) return;

  class TttFbaBuybox extends HTMLElement {
    connectedCallback() {
      this.picks = []; // one entry per tin, in click order: variant id strings
      this.adding = false;
      this.tiers = [...this.querySelectorAll('[data-fbb-tier]')];
      this.rows = [...this.querySelectorAll('[data-fbb-scent-row]')];
      this.cta = this.querySelector('[data-fbb-cta]');
      this.ctaLabel = this.querySelector('[data-fbb-cta-label]');
      this.ctaCompare = this.querySelector('[data-fbb-compare]');
      this.ctaPrice = this.querySelector('[data-fbb-price]');
      this.error = this.querySelector('[data-fbb-error]');
      this.baseCents = Number(this.dataset.baseCents) || 0;
      this.priceById = {};
      this.rows.forEach((r) => {
        this.priceById[r.dataset.variantId] = Number(r.dataset.priceCents) || this.baseCents;
      });

      // If the merchant left every tier unpreselected, nothing is checked but
      // `tier` would still fall back to the first one — pricing and gating off
      // a tier no card shows as selected. Check it for real instead.
      if (this.tiers.length && !this.tiers.some((t) => t.checked)) {
        this.tiers[0].checked = true;
      }

      // The tier the page opens on is a floor, not just a starting point:
      // picking one tin when the card says 2x shouldn't silently downgrade
      // the deal the customer was shown. Only picks above it move the tier.
      this.floor = this.tier.quantity;

      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-fbb-tier]')) this.onTierPick();
      });

      // Gallery preview. Hover is a preview and only offered where hovering is
      // a real gesture; tapping a row on a phone arrives as a click, so the
      // pin path covers touch on its own.
      this.pinnedRow = null;
      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        this.rows.forEach((row) => {
          row.addEventListener('mouseenter', () => this.previewGallery(row));
          row.addEventListener('mouseleave', () => this.previewGallery(null));
        });
      }
      // Any interaction with the gallery itself wins: the customer is driving
      // it directly, so a stale pinned overlay would fight them.
      const gallery = document.querySelector('media-gallery');
      if (gallery) gallery.addEventListener('click', () => this.pinGallery(null));

      this.syncTier();
    }

    // The wrapper of whichever slide is showing right now, resolved per call
    // so sliding the carousel between previews lands the overlay correctly.
    get galleryStage() {
      const gallery = document.querySelector('media-gallery');
      if (!gallery) return null;
      return (
        gallery.querySelector('.product__media-item.is-active .product__media') ||
        gallery.querySelector('.product__media-item .product__media')
      );
    }

    previewGallery(row) {
      const target = row || this.pinnedRow;
      this.rows.forEach((r) => r.classList.toggle('is-previewing', r === row && r !== this.pinnedRow));
      this.paintGallery(target);
    }

    pinGallery(row) {
      this.pinnedRow = this.pinnedRow === row ? null : row;
      this.rows.forEach((r) => r.classList.toggle('is-pinned', r === this.pinnedRow));
      if (!this.pinnedRow) this.rows.forEach((r) => r.classList.remove('is-previewing'));
      this.paintGallery(this.pinnedRow);
    }

    paintGallery(row) {
      const src = row && row.dataset.gallerySrc;
      const stage = this.galleryStage;
      if (!stage) return;
      const existing = stage.parentElement.querySelector('.ttt-fbb__gallery-swap');
      // Clearing has to sweep every stage, not just the current one — the
      // overlay may have been left on a slide that has since scrolled away.
      if (!src) {
        document.querySelectorAll('.ttt-fbb__gallery-swap').forEach((el) => el.remove());
        return;
      }
      document.querySelectorAll('.ttt-fbb__gallery-swap').forEach((el) => {
        if (el !== existing) el.remove();
      });
      const img = existing || document.createElement('img');
      if (!existing) {
        img.className = 'ttt-fbb__gallery-swap';
        img.decoding = 'async';
        stage.appendChild(img);
      }
      // "match" copies however the section was told to fit its own media, so
      // the swapped shot is framed like the slide it covers rather than
      // jumping; an explicit fit overrides that.
      const fit = this.dataset.swapFit || 'match';
      if (fit === 'match') {
        const under = stage.querySelector('img:not(.ttt-fbb__gallery-swap)');
        img.style.objectFit = (under && getComputedStyle(under).objectFit) || 'cover';
      } else {
        img.style.objectFit = fit;
      }
      img.style.objectPosition = row.dataset.galleryPos || '50% 50%';
      // The overlay lives inside the gallery, not inside this element, so the
      // zoom custom property set on the buybox root can't inherit down to it —
      // it has to be read here and written onto the overlay directly.
      const zoom = getComputedStyle(this).getPropertyValue('--fbb-swap-zoom').trim();
      img.style.transform = `scale(${zoom || 1})`;
      img.alt = row.dataset.galleryAlt || '';
      if (img.getAttribute('src') === src) {
        img.classList.add('is-ready');
        return;
      }
      img.classList.remove('is-ready');
      img.setAttribute('src', src);
      const reveal = () => {
        // The row may have been left (or another pinned) while this decoded —
        // only reveal if this image is still the one being asked for.
        if (img.isConnected && img.getAttribute('src') === src) img.classList.add('is-ready');
      };
      if (img.complete && img.naturalWidth) reveal();
      else img.addEventListener('load', reveal, { once: true });
    }

    get tier() {
      const checked = this.tiers.find((t) => t.checked) || this.tiers[0];
      return {
        quantity: Number(checked?.dataset.quantity) || 1,
        percent: Number(checked?.dataset.percent) || 0,
      };
    }

    onClick(e) {
      const add = e.target.closest('[data-fbb-add]');
      const minus = e.target.closest('[data-fbb-minus]');
      if (add) this.addPick(add.closest('[data-fbb-scent-row]'));
      if (minus) this.removePick(minus.closest('[data-fbb-scent-row]'));
      if (e.target.closest('[data-fbb-cta]')) this.addToCart();

      // Clicking the row body pins its shot in the gallery. The steppers are
      // excluded: adding a tin shouldn't quietly hijack the gallery too.
      if (add || minus) return;
      const row = e.target.closest('[data-fbb-scent-row]');
      if (row && row.dataset.gallerySrc) this.pinGallery(row);
    }

    addPick(row) {
      if (!row || row.dataset.available !== 'true') return;
      this.picks.push(row.dataset.variantId);
      this.syncTier();
    }

    removePick(row) {
      const i = this.picks.lastIndexOf(row.dataset.variantId);
      if (i > -1) this.picks.splice(i, 1);
      this.syncTier();
    }

    // The richest tier a given tin count reaches. Past the largest card the
    // largest card stays selected, so the quoted saving stops at the biggest
    // one these cards can name — the cart's own quantity break keeps climbing
    // past that, which means the customer is never charged more than quoted.
    tierFor(count, visibleOnly) {
      const pool = visibleOnly ? this.tiers.filter((t) => !t.hasAttribute('data-cardless')) : this.tiers;
      const bySize = [...pool].sort((a, b) => (Number(a.dataset.quantity) || 0) - (Number(b.dataset.quantity) || 0));
      let best = bySize[0];
      bySize.forEach((t) => {
        if ((Number(t.dataset.quantity) || 0) <= count) best = t;
      });
      return best;
    }

    // Single path for every tier move — adds, removes, and the initial
    // render — so the checked radio, the card highlight and the pricing can
    // never disagree about which tier is live.
    syncTier() {
      const target = Math.max(this.floor, this.picks.length);
      const want = this.tierFor(target, false);
      if (want) want.checked = true;
      // A cardless rung has no card to light up, so the richest tier the
      // customer can actually see stays marked instead. The CTA still quotes
      // the real rung, which is the number that has to be right.
      const shown = want && !want.hasAttribute('data-cardless') ? want : this.tierFor(target, true);
      this.querySelectorAll('.ttt-fbb__tier').forEach((label) => {
        label.classList.toggle('is-selected', label.querySelector('input') === shown);
      });
      this.render();
    }

    onTierPick() {
      // Clicking a card re-targets the bundle in both directions: it sets a
      // new floor, and picks above the chosen size fall away.
      this.floor = this.tier.quantity;
      this.picks = this.picks.slice(0, this.tier.quantity);
      this.syncTier();
    }

    render() {
      const { quantity, percent } = this.tier;
      const picked = this.picks.length;
      this.rows.forEach((row) => {
        const count = this.picks.filter((id) => id === row.dataset.variantId).length;
        const countEl = row.querySelector('[data-fbb-count]');
        countEl.textContent = count;
        countEl.hidden = count === 0;
        row.querySelector('[data-fbb-minus]').hidden = count === 0;
        // Nothing caps the tin count any more — a full bundle is a floor to
        // keep adding from, not a stop.
        row.querySelector('[data-fbb-add]').disabled = row.dataset.available !== 'true';
        row.classList.toggle('is-picked', count > 0);
      });

      const remaining = Math.max(quantity - picked, 0);
      if (remaining > 0) {
        const tpl = remaining === 1 ? this.dataset.labelRemainingOne : this.dataset.labelRemainingMany;
        this.ctaLabel.textContent = (tpl || '').replace('{count}', remaining);
      } else {
        // Counts the tins actually picked, not the tier's size, so the button
        // stays honest once the bundle runs past the largest card.
        const readyTpl = picked === 1 ? this.dataset.labelReadyOne || this.dataset.labelReady : this.dataset.labelReady;
        this.ctaLabel.textContent = (readyTpl || '').replace('{count}', picked);
      }
      this.cta.disabled = remaining > 0;

      // Projected full-tier totals: picked variants at their real price,
      // unpicked slots projected at the base price (the design shows totals
      // even while picks are incomplete).
      const units = this.picks.map((id) => this.priceById[id]);
      for (let i = 0; i < remaining; i += 1) units.push(this.baseCents);
      const compare = units.reduce((sum, c) => sum + c, 0);
      const price = units.reduce((sum, c) => sum + this.discountUnit(c, percent), 0);
      this.ctaCompare.textContent = percent > 0 ? this.formatMoney(compare) : '';
      this.ctaPrice.textContent = this.formatMoney(price);
    }

    // Shopify allocates percentage discounts per unit and truncates the
    // per-unit discount to whole cents (allocation_method "each"), so mirror
    // that here — rounding the line total instead drifts a cent or two from
    // what the cart actually charges.
    discountUnit(cents, percent) {
      return cents - Math.floor((cents * percent) / 100);
    }

    // Mirrors Shopify's money_format tokens rather than always emitting
    // "1234.56": the shop's format decides the separators and whether cents
    // show, and formats can be wrapped in markup, which textContent would
    // otherwise print literally.
    formatMoney(cents) {
      const fmt = this.dataset.moneyFormat || '${{amount}}';
      const group = (whole, sep) => whole.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
      const build = (token) => {
        const dotDecimal = !token.includes('no_decimals');
        const commaDecimal = token.includes('comma_separator');
        const value = cents / 100;
        if (!dotDecimal) return group(String(Math.round(value)), commaDecimal ? '.' : ',');
        const [whole, frac] = value.toFixed(2).split('.');
        return `${group(whole, commaDecimal ? '.' : ',')}${commaDecimal ? ',' : '.'}${frac}`;
      };
      const rendered = fmt.replace(/\{\{\s*(amount[a-z_]*)\s*\}\}/g, (_, token) => build(token));
      // Strip any markup the format carries, since this lands in textContent.
      return rendered.replace(/<[^>]*>/g, '').trim();
    }

    async addToCart() {
      if (this.adding || this.picks.length < this.tier.quantity) return;
      this.adding = true;
      this.cta.classList.add('is-loading');
      this.error.hidden = true;

      // Collapse picks into { variantId: qty } line items.
      const lines = {};
      this.picks.forEach((id) => {
        lines[id] = (lines[id] || 0) + 1;
      });
      const afterAdd = this.dataset.afterAdd || 'drawer';
      // Only the drawer path needs rendered sections back — asking for them
      // when we're about to leave the page is a wasted round trip.
      const cartDrawer = afterAdd === 'drawer' ? document.querySelector('cart-drawer') : null;
      const body = { items: Object.entries(lines).map(([id, quantity]) => ({ id: Number(id), quantity })) };
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        body.sections = cartDrawer.getSectionsToRender().map((s) => s.id);
        body.sections_url = window.location.pathname;
      }

      try {
        const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
        const res = await fetch(`${addUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await res.json().catch(() => ({}));
        // Shopify cart errors can arrive as HTTP 200 with a `status` field
        // (see Dawn's product-form.js) — treat both shapes as failure.
        if (!res.ok || data.status) {
          throw new Error(data.description || data.message || 'Unable to add to cart.');
        }
        this.picks = [];
        this.syncTier();
        // Bare lexical globals, matching ttt-bundle-builder.js.
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ttt-fba-buybox', cartData: data });
        }
        if (afterAdd === 'checkout') {
          window.location.href = '/checkout';
        } else if (cartDrawer && data.sections) {
          cartDrawer.renderContents(data);
        } else {
          window.location.href = (window.routes && window.routes.cart_url) || '/cart';
        }
      } catch (err) {
        this.error.textContent = err.message;
        this.error.hidden = false;
      } finally {
        this.adding = false;
        this.cta.classList.remove('is-loading');
      }
    }
  }

  customElements.define('ttt-fba-buybox', TttFbaBuybox);
})();
